import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Check } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Reveal, Kicker } from "@/components/Reveal";
import { MembershipModal } from "@/components/MembershipModal";
import { content as defaultContent } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

const Hero = ({ content, onJoin }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative h-[100svh] overflow-hidden bg-[#1A3B2A]">
      <motion.div style={{ y: imgY, scale }} className="absolute inset-0">
        <img src={content.hero.image} alt="Sundsvalls natur" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f2419]/70 via-[#0f2419]/40 to-[#0f2419]/85" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex flex-col justify-end pb-20 sm:pb-28">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          <Kicker dark>{content.hero.kicker}</Kicker>
        </motion.div>

        <h1 data-testid="hero-title" className="font-display text-[#F7F7F4] mt-5 text-6xl sm:text-8xl lg:text-[9rem] leading-[0.92] font-light tracking-tight">
          {content.hero.lines.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.4 + i * 0.13, ease }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1, ease }}
          className="mt-8 max-w-xl text-[#F7F7F4]/80 text-base sm:text-lg leading-relaxed"
        >
          {content.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-join-btn"
            onClick={onJoin}
            className="inline-flex items-center gap-2 rounded-full bg-[#F7F7F4] text-[#1A3B2A] px-7 py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#C2D3C4] active:scale-95 transition-[background-color,transform] duration-300"
          >
            {content.hero.cta} <ArrowUpRight size={18} />
          </button>
          <a
            href="#hur"
            data-testid="hero-how-link"
            className="inline-flex items-center gap-2 rounded-full border border-[#F7F7F4]/40 text-[#F7F7F4] px-7 py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#F7F7F4]/10 transition-colors duration-300"
          >
            {content.hero.ctaSecondary}
          </a>
        </motion.div>
      </motion.div>

      <motion.div style={{ opacity: fade }} className="absolute bottom-8 right-6 sm:right-12 z-10 text-[#F7F7F4]/70">
        <ArrowDown className="animate-bounce" />
      </motion.div>
    </section>
  );
};

const Marquee = ({ content }) => {
  const items = [...content.marquee, ...content.marquee];
  return (
    <div className="bg-[#C2D3C4] border-y border-[#1A3B2A]/10 py-5 overflow-hidden">
      <div className="bp-marquee-track">
        {items.map((t, i) => (
          <span key={i} className="flex items-center font-display text-3xl sm:text-4xl text-[#1A3B2A] px-8">
            {t} <span className="text-[#4A6D56] px-8">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
};

const HowItWorks = ({ content }) => (
  <section id="hur" className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
    <Reveal><Kicker>{content.manifesto.kicker}</Kicker></Reveal>
    <Reveal delay={0.05}>
      <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#1A3B2A] mt-5 max-w-2xl">
        {content.manifesto.title}
      </h2>
    </Reveal>
    <div className="mt-16 grid md:grid-cols-3 gap-6">
      {content.manifesto.steps.map((s, i) => (
        <Reveal key={i} delay={i * 0.12}>
          <div
            data-testid={`step-${s.n}`}
            className="relative h-full rounded-3xl border border-[#D1D8D3] bg-white p-8 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_-25px_rgba(26,59,42,0.5)] transition-[transform,box-shadow] duration-500"
          >
            <span className="font-display text-7xl sm:text-8xl text-[#C2D3C4] leading-none">{s.n}</span>
            <h3 className="text-xl sm:text-2xl font-semibold text-[#1A3B2A] mt-4">{s.title}</h3>
            <p className="text-[#1B1F1C]/70 mt-3 leading-relaxed">{s.text}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const CarShowcase = ({ content }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "8%"]);
  return (
    <section id="bilen" ref={ref} className="relative bg-[#1A3B2A] text-[#F7F7F4] bp-grain overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <Reveal><Kicker dark>{content.car.kicker}</Kicker></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light mt-5">
              {content.car.title}
              <span className="block text-[#C2D3C4] text-2xl sm:text-3xl mt-3 tracking-wide">
                {content.car.year} · {content.car.fuel}
              </span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-[#F7F7F4]/75 mt-6 max-w-md leading-relaxed">{content.car.text}</p>
          </Reveal>
          <div className="mt-8 flex flex-wrap gap-3">
            {content.car.specs.map((sp, i) => (
              <Reveal key={i} delay={0.12 + i * 0.05}>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 text-sm">
                  <Check size={15} className="text-[#C2D3C4]" /> {sp}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={0.15}>
          <div className="relative">
            <motion.div style={{ y }} className="rounded-3xl overflow-hidden border border-white/15 shadow-2xl">
              <img src={content.car.image} alt="Skoda Octavia" className="w-full h-[420px] object-cover" />
            </motion.div>
            <div className="absolute -bottom-6 -left-4 sm:left-8 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 px-6 py-4">
              <p className="text-3xl font-display text-[#C2D3C4]">{content.car.fuel.split(" ")[0]}</p>
              <p className="text-xs uppercase tracking-widest text-[#F7F7F4]/70">Bränslesnål</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const PriceCol = ({ title, rows, dark }) => (
  <div
    className={`rounded-3xl p-8 sm:p-10 ${
      dark ? "bg-[#1A3B2A] text-[#F7F7F4]" : "bg-white border border-[#D1D8D3] text-[#1B1F1C]"
    }`}
  >
    <h3 className={`font-display text-3xl mb-6 ${dark ? "text-[#C2D3C4]" : "text-[#1A3B2A]"}`}>{title}</h3>
    <ul className="divide-y divide-current/10">
      {rows.map((r, i) => (
        <li key={i} className="py-4 flex items-baseline justify-between gap-4">
          <span className="flex flex-col">
            <span className="font-medium">{r.label}</span>
            {r.note && <span className={`text-xs mt-0.5 ${dark ? "text-[#F7F7F4]/50" : "text-[#4A6D56]"}`}>{r.note}</span>}
          </span>
          <span className={`font-display text-2xl whitespace-nowrap ${dark ? "text-[#F7F7F4]" : "text-[#1A3B2A]"}`}>
            {r.value}
          </span>
        </li>
      ))}
    </ul>
  </div>
);

const Pricing = ({ content }) => (
  <section id="priser" className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
    <Reveal><Kicker>{content.pricing.kicker}</Kicker></Reveal>
    <Reveal delay={0.05}>
      <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#1A3B2A] mt-5 max-w-2xl">
        {content.pricing.title}
      </h2>
    </Reveal>
    <div className="mt-14 grid md:grid-cols-2 gap-6">
      <Reveal><PriceCol title="Att gå med" rows={content.pricing.join} /></Reveal>
      <Reveal delay={0.1}><PriceCol title="När du kör" rows={content.pricing.usage} dark /></Reveal>
    </div>
  </section>
);

const StudentOffer = ({ content }) => (
  <section id="student" className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pb-24 sm:pb-32">
    <div className="grid lg:grid-cols-2 rounded-3xl overflow-hidden border border-[#D1D8D3]">
      <Reveal className="bg-[#C2D3C4] p-10 sm:p-14 flex flex-col justify-center">
        <Kicker>{content.student.kicker}</Kicker>
        <h2 className="font-display text-4xl sm:text-5xl font-light text-[#1A3B2A] mt-5">{content.student.title}</h2>
        <p className="text-[#1B1F1C]/75 mt-5 leading-relaxed">{content.student.text}</p>
        <div className="mt-8 inline-flex w-fit items-center gap-3 rounded-full bg-[#1A3B2A] text-[#F7F7F4] px-6 py-3">
          <span className="font-display text-2xl">{content.student.highlight}</span>
        </div>
      </Reveal>
      <div className="relative min-h-[320px] lg:min-h-full overflow-hidden">
        <img src={content.student.image} alt="Student i Sundsvall" className="absolute inset-0 h-full w-full object-cover" />
      </div>
    </div>
  </section>
);

const Sustainability = ({ content }) => (
  <section className="relative bg-[#4A6D56] text-[#F7F7F4] overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32 grid lg:grid-cols-2 gap-14 items-center">
      <Reveal>
        <div className="rounded-3xl overflow-hidden border border-white/15">
          <img src={content.sustainability.image} alt="Hållbarhet" className="w-full h-[380px] object-cover" />
        </div>
      </Reveal>
      <div>
        <Reveal><Kicker dark>{content.sustainability.kicker}</Kicker></Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light mt-5">{content.sustainability.title}</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-[#F7F7F4]/80 mt-6 leading-relaxed max-w-md">{content.sustainability.text}</p>
        </Reveal>
        <div className="mt-10 grid grid-cols-3 gap-4">
          {content.sustainability.stats.map((s, i) => (
            <Reveal key={i} delay={0.15 + i * 0.08}>
              <div>
                <p className="font-display text-4xl sm:text-5xl text-[#C2D3C4]">{s.value}</p>
                <p className="text-xs uppercase tracking-widest text-[#F7F7F4]/70 mt-2">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Contact = ({ content, onJoin }) => (
  <section id="kontakt" className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
    <Reveal><Kicker>{content.contact.kicker}</Kicker></Reveal>
    <Reveal delay={0.05}>
      <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#1A3B2A] mt-5 max-w-2xl">
        {content.contact.title}
      </h2>
    </Reveal>
    <div className="mt-14 grid md:grid-cols-2 gap-6">
      {content.contact.people.map((p, i) => (
        <Reveal key={i} delay={i * 0.1}>
          <a
            href={`mailto:${p.email}`}
            data-testid={`contact-${i}`}
            className="group block rounded-3xl border border-[#D1D8D3] bg-white p-8 hover:border-[#1A3B2A] transition-colors duration-300"
          >
            <p className="font-display text-3xl text-[#1A3B2A]">{p.name}</p>
            <p className="text-[#4A6D56] mt-2 flex items-center gap-2">
              {p.email} <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
            </p>
            {p.note && <p className="text-sm text-[#1B1F1C]/50 mt-3">{p.note}</p>}
          </a>
        </Reveal>
      ))}
    </div>
    <Reveal delay={0.2}>
      <div className="mt-6 rounded-3xl bg-[#1A3B2A] text-[#F7F7F4] p-10 sm:p-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <p className="font-display text-3xl sm:text-4xl max-w-lg font-light">
          Redo att dela en bil och hjälpa oss köpa nästa?
        </p>
        <button
          data-testid="contact-join-btn"
          onClick={onJoin}
          className="inline-flex items-center gap-2 rounded-full bg-[#F7F7F4] text-[#1A3B2A] px-7 py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#C2D3C4] active:scale-95 transition-[background-color,transform] duration-300"
        >
          Bli medlem <ArrowUpRight size={18} />
        </button>
      </div>
    </Reveal>
  </section>
);

const Footer = ({ content }) => (
  <footer className="border-t border-[#D1D8D3] py-10">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
      <p className="font-display text-2xl text-[#1A3B2A]">{content.brand}</p>
      <div className="flex items-center gap-6">
        <a href="/admin" data-testid="footer-admin-link" className="text-sm font-medium text-[#4A6D56] hover:text-[#1A3B2A] transition-colors">
          Admin
        </a>
        <p className="text-sm text-[#1B1F1C]/50">© {new Date().getFullYear()} · Bildelning i Sundsvall sedan {content.since}</p>
      </div>
    </div>
  </footer>
);

export default function Landing() {
  const [content, setContent] = useState(defaultContent);
  const [joinOpen, setJoinOpen] = useState(false);
  const onJoin = () => setJoinOpen(true);

  useEffect(() => {
    fetch(`${API}/content`)
      .then((r) => r.json())
      .then((d) => d && d.hero && setContent(d))
      .catch(() => {});
  }, []);

  return (
    <main className="bg-[#F7F7F4]">
      <Nav content={content} onJoin={onJoin} />
      <Hero content={content} onJoin={onJoin} />
      <Marquee content={content} />
      <HowItWorks content={content} />
      <CarShowcase content={content} />
      <Pricing content={content} />
      <StudentOffer content={content} />
      <Sustainability content={content} />
      <Contact content={content} onJoin={onJoin} />
      <Footer content={content} />
      <MembershipModal open={joinOpen} onClose={() => setJoinOpen(false)} />
    </main>
  );
}
